import React, { useMemo } from "react";
import CardMenu from "components/card/CardMenu";
import Card from "components/card";
import { MdAdminPanelSettings, MdPerson } from "react-icons/md";
import avatarDefault from "assets/img/avatars/avatar4.png";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

const R2 = process.env.REACT_APP_R2_PUBLIC_URL || "https://pub-26704679c67f47a28bce53f5dac3f586.r2.dev";
const columnHelper = createColumnHelper();

export default function UsersTable(props) {
  const { tableData } = props;
  const [sorting, setSorting] = React.useState([]);
  
  const columns = useMemo(() => [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">USER</p>
      ),
      cell: (info) => {
        const user = info.row.original;
        const nameOrEmail = info.getValue() || user.email.split('@')[0];
        const initial = nameOrEmail.charAt(0).toUpperCase();
        
        let currentAvatar = null;
        if (user.avatarUrl) {
          currentAvatar = user.avatarUrl.startsWith('http') ? user.avatarUrl : `${R2}/${user.avatarUrl}`;
        }

        return (
          <div className="flex items-center gap-3">
            {currentAvatar ? (
              <img src={currentAvatar} alt="" className="h-10 w-10 rounded-full object-cover bg-white" />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-lg font-bold text-white">
                {initial}
              </div>
            )}
            <p className="text-sm font-bold text-navy-700 dark:text-white">
              {nameOrEmail}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">EMAIL</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("role", {
      id: "role",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ROLE</p>
      ),
      cell: (info) => (
        <div className="flex items-center gap-1">
          {info.getValue() === "ADMIN" ? (
            <MdAdminPanelSettings className="text-brand-500" />
          ) : (
            <MdPerson className="text-gray-500" />
          )}
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">JOINED</p>
      ),
      cell: (info) => {
        const date = new Date(info.getValue());
        return (
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {date.toLocaleDateString()}
          </p>
        );
      },
    }),
  ], []);

  const table = useReactTable({
    data: tableData || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <Card extra={"w-full h-full px-6 pb-6 sm:overflow-x-auto"}>
      <div className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Platform Users
        </div>
        <CardMenu />
      </div>

      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start"
                  >
                    <div className="items-center justify-between text-xs text-gray-200">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{ asc: " ↑", desc: " ↓" }[header.column.getIsSorted()] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="min-w-[150px] border-white/0 py-3 pr-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={4} className="py-4 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {table.getPageCount() > 1 && (
        <div className="mt-4 flex w-full items-center justify-between px-4 pb-2">
          <div className="text-sm text-gray-600 dark:text-white">
            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getPrePaginationRowModel().rows.length
            )}{" "}
            of {table.getPrePaginationRowModel().rows.length} entries
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="linear rounded-[20px] bg-lightPrimary px-4 py-2 text-sm font-bold text-brand-500 transition duration-200 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20"
            >
              Prev
            </button>
            <div className="text-sm font-bold text-navy-700 dark:text-white">
              {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
            </div>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="linear rounded-[20px] bg-brand-500 px-4 py-2 text-sm font-bold text-white transition duration-200 hover:bg-brand-600 disabled:opacity-50 dark:bg-brand-400"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
